import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import { SearchBox } from '../components';
import { useDebounce } from 'app/hooks/useDebounce';
import { getDefaultThumbnail } from 'app/pages/Equipment/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  selectCategoryCounts,
  selectCompatibleIdsByMachineId,
  selectModelById,
  selectModelsBySearchQueryAndCategory,
  selectRootCategories,
  selectVisibleCategoryIds,
} from 'services/api/resources/selectors';
import { selectRecentlyViewedModels } from 'services/slices/resourcesHistory';
import { useAppSelector } from 'services/store';
import ChevronRightIcon from 'assets/images/chevron_right.svg';
import { ResourceFilters } from 'services/api/resources/types';
import './RootCategoriesPage.less';

export default function RootCategoriesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const filterMachineId = searchParams.get('compatibleWith');

  const model = useAppSelector(state =>
    selectModelById(state, filterMachineId ?? ''),
  );

  const compatibleIds = useAppSelector(state =>
    selectCompatibleIdsByMachineId(state, filterMachineId),
  );

  const filters: ResourceFilters = useMemo(
    () => ({
      compatibleIds,
    }),
    [compatibleIds],
  );

  const visibleCategorySet = useAppSelector(state =>
    selectVisibleCategoryIds(state, filters),
  );

  const categoryCounts = useAppSelector(state =>
    selectCategoryCounts(state, filters),
  );

  const rootCategories = useAppSelector(selectRootCategories);
  const recentModels = useAppSelector(selectRecentlyViewedModels);

  const visibleRoots = React.useMemo(() => {
    if (!visibleCategorySet) return rootCategories;

    return rootCategories.filter(cat => visibleCategorySet.has(cat.id));
  }, [rootCategories, visibleCategorySet]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const searchResults = useAppSelector(state =>
    selectModelsBySearchQueryAndCategory(
      state,
      debouncedSearchTerm,
      undefined,
      filters,
    ),
  );

  const isAttachmentType = model?.type === 'attachment';

  const title = t(
    filterMachineId
      ? isAttachmentType
        ? 'compatible_machines'
        : 'compatible_attachments'
      : 'title_resources',
  );

  const subtitle = filterMachineId
    ? t('model_compatible_with', { s: filterMachineId })
    : t('browse_machines_attachments');

  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);
  useEffect(() => {
    let index: number = setClassNames('layout-grey');
    return () => removeClassNames(index);
  }, []);

  useEffect(() => {
    if (visibleRoots.length === 1) {
      const singleCategory = visibleRoots[0];

      console.log(
        `Auto-redirecting to single category: ${singleCategory.name}`,
      );

      navigate(
        {
          pathname: `/resources/${singleCategory.slug}`,
          search: searchParams.toString(),
        },
        {
          replace: true,
        },
      );
    }
  }, [visibleRoots, navigate, searchParams]);

  const buildLink = (slug: string) => ({
    pathname: `/resources${slug}`,
    search: searchParams.toString(),
  });

  return (
    <div className="root-categories-page">
      <div className="title-with-search root-categories-page__titles-block">
        <div className="title-box">
          <h1 className="root-categories-page__title">{title}</h1>
          <h2 className="root-categories-page__subtitle">{subtitle}</h2>
        </div>

        <SearchBox
          searchTerm={searchTerm}
          searchFor={t('title_resources')}
          searchResults={searchResults}
          setSearchTerm={setSearchTerm}
        />
      </div>

      <div>
        {!filterMachineId &&
          (!!rootCategories.length ? (
            <div className="row-0 resources-boxes">
              {rootCategories.map(cat => {
                return (
                  <Link
                    to={buildLink(`/${cat.slug}`)}
                    className="col-sm-4 col-xxs-6 r-box-wrapper"
                    key={cat.id}
                  >
                    <div className="r-img">
                      <img src={cat.iconUrl} />
                    </div>

                    <h4>{cat.name}</h4>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center">
              {t('resources.errors.no_resources_found')}
            </div>
          ))}

        {filterMachineId &&
          (!!visibleRoots.length ? (
            <div className="add-equipment-boxes resources-style">
              {visibleRoots.map(cat => {
                const count = categoryCounts[cat.id] || 0;

                return (
                  <span key={cat.id}>
                    <div className="equipment-content-actions section">
                      <div
                        className="add-equipment-item"
                        onClick={() => {
                          navigate(buildLink(`/${cat.slug}`));
                        }}
                      >
                        <div className="product-length">
                          <span>{count}</span>
                        </div>

                        <div className="add-equipment-box-img">
                          <img
                            src={
                              cat.iconUrl ||
                              getDefaultThumbnail(
                                filterMachineId ? 'attachment' : cat.name,
                                filterMachineId ? 'attachment' : cat.name,
                              )
                            }
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null;
                              currentTarget.src = getDefaultThumbnail(
                                filterMachineId ? 'attachment' : cat.name,
                                filterMachineId ? 'attachment' : cat.name,
                              );
                            }}
                            alt="Category"
                          />
                        </div>

                        <h3>{cat.name}</h3>
                        <img
                          className="arrow-links_icon arrow-links_icon-right"
                          src={ChevronRightIcon}
                        />
                      </div>
                    </div>
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="text-center">
              {t('resources.errors.no_compatible_models_found')}
            </div>
          ))}

        {!!recentModels.length && !filterMachineId && (
          <>
            <div className="separator-xl"></div>
            <h3>{t('recently_viewed')}</h3>
            <div className="recently-view">
              <div className="row">
                {recentModels.map(({ model, type, modelIconUrl }) => (
                  <Link
                    key={model}
                    to={`/resources/check-redirect/p/${model}`}
                    className="col-sm-3 col-xs-6"
                  >
                    <div className="recently-view_item">
                      <img
                        src={modelIconUrl || getDefaultThumbnail(type)}
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null;
                          currentTarget.src = getDefaultThumbnail(type);
                        }}
                        alt=""
                      />
                      <h4 title={model}>{model}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
